import { useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import Tabs from '@/components/base/Tabs';
import Card from '@/components/base/Card';
import Badge from '@/components/base/Badge';
import { useData } from '@/context/DataContext';
import { formatTL } from '@/lib/format';

function Bar({ value, max, tone = 'bg-primary-500' }: { value: number; max: number; tone?: string }) {
  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-background-200">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export default function Reports() {
  const { quotes, projects, collections, payments, receivables, projectFinances, projectName } = useData();
  const [tab, setTab] = useState('satis');

  const sentQuotes = quotes.length;
  const wonQuotes = quotes.filter((q) => q.status === 'Onaylandı').length;
  const lostQuotes = quotes.filter((q) => q.status === 'Kaybedildi').length;
  const conversionRate = sentQuotes > 0 ? Math.round((wonQuotes / sentQuotes) * 100) : 0;
  const totalQuoteAmount = quotes.reduce((s, q) => s + q.amount, 0);
  const wonAmount = quotes.filter((q) => q.status === 'Onaylandı').reduce((s, q) => s + q.amount, 0);

  const activeProjects = projects.filter((p) => p.status !== 'Tamamlandı');
  const completedProjects = projects.filter((p) => p.status === 'Tamamlandı');
  const overdueProjects = projects.filter((p) => p.status === 'Gecikti');
  const avgDuration = completedProjects.length > 0
    ? Math.round(completedProjects.reduce((s, p) => s + ((new Date(p.plannedDelivery).getTime() - new Date(p.startDate).getTime()) / 86400000), 0) / completedProjects.length)
    : 0;

  const collectedMonth = collections.filter((c) => c.date >= '2026-08-01').reduce((s, c) => s + c.amount, 0);
  const paidMonth = payments.filter((p) => p.date >= '2026-08-01').reduce((s, p) => s + p.amount, 0);
  const totalReceivable = receivables.reduce((s, r) => s + r.remaining, 0);
  const totalPayments = payments.reduce((s, p) => s + p.amount, 0);

  const categoryStats = projects.reduce<Record<string, { count: number; total: number }>>((acc, p) => {
    if (!acc[p.category]) acc[p.category] = { count: 0, total: 0 };
    acc[p.category].count += 1;
    acc[p.category].total += p.salesAmount;
    return acc;
  }, {});
  const categoryList = Object.entries(categoryStats)
    .map(([category, v]) => ({ category, ...v }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);
  const maxCategoryTotal = Math.max(...categoryList.map((c) => c.total), 1);

  const maxProfit = Math.max(...projectFinances.map((pf) => pf.grossProfit), 1);

  const tabs = [
    { key: 'satis', label: 'Satış Raporu' },
    { key: 'proje', label: 'Proje Raporu' },
    { key: 'finans', label: 'Finans Raporu' },
    { key: 'tur', label: 'İş Türü Analizi' },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader title="Raporlar" description="Satış, proje, finans ve iş türü performansını analiz edin." />

      <Tabs items={tabs} value={tab} onChange={setTab} />

      {tab === 'satis' && (
        <div className="space-y-5">
          <div className="stagger grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {[
              { label: 'Verilen Teklif', value: String(sentQuotes) },
              { label: 'Kazanılan Teklif', value: String(wonQuotes) },
              { label: 'Kaybedilen Teklif', value: String(lostQuotes) },
              { label: 'Dönüşüm Oranı', value: `%${conversionRate}` },
              { label: 'Toplam Teklif Tutarı', value: formatTL(totalQuoteAmount) },
              { label: 'Kazanılan İş Tutarı', value: formatTL(wonAmount) },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{kpi.label}</p>
                <p className="mt-2 font-heading text-xl font-semibold text-foreground-950">{kpi.value}</p>
              </div>
            ))}
          </div>
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Teklif Dağılımı</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {['Taslak', 'Hazırlanıyor', 'Gönderildi', 'Görüşmede', 'Revize', 'Onaylandı', 'Kaybedildi', 'İptal'].map((s) => {
                const count = quotes.filter((q) => q.status === s).length;
                return (
                  <div key={s} className="flex items-center justify-between rounded-md bg-background-100 px-4 py-3">
                    <span className="text-sm text-foreground-700">{s}</span>
                    <span className="font-heading text-lg font-semibold text-foreground-950">{count}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {tab === 'proje' && (
        <div className="space-y-5">
          <div className="stagger grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: 'Devam Eden', value: String(activeProjects.length), tone: 'text-primary-700' },
              { label: 'Tamamlanan', value: String(completedProjects.length), tone: 'text-foreground-950' },
              { label: 'Geciken', value: String(overdueProjects.length), tone: 'text-accent-700' },
              { label: 'Ortalama Proje Süresi', value: `${avgDuration} gün`, tone: 'text-foreground-950' },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{kpi.label}</p>
                <p className={`mt-2 font-heading text-xl font-semibold ${kpi.tone}`}>{kpi.value}</p>
              </div>
            ))}
          </div>
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Proje İlerleme Durumu</h2>
            <div className="mt-4 space-y-4">
              {activeProjects.map((p) => (
                <div key={p.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground-900">{p.name}</span>
                    <span className="text-foreground-500">%{p.progress}</span>
                  </div>
                  <Bar value={p.progress} max={100} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === 'finans' && (
        <div className="space-y-5">
          <div className="stagger grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: 'Bu Ay Tahsilat', value: formatTL(collectedMonth) },
              { label: 'Bu Ay Ödeme', value: formatTL(paidMonth) },
              { label: 'Toplam Alacak', value: formatTL(totalReceivable) },
              { label: 'Toplam Borç', value: formatTL(totalPayments) },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{kpi.label}</p>
                <p className="mt-2 font-heading text-xl font-semibold text-foreground-950">{kpi.value}</p>
              </div>
            ))}
          </div>
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Proje Bazlı Kârlılık</h2>
            <div className="mt-4 space-y-4">
              {[...projectFinances].sort((a, b) => b.grossProfit - a.grossProfit).map((pf) => (
                <div key={pf.projectId}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground-900">{projectName(pf.projectId)}</span>
                    <span className="text-foreground-500">{formatTL(pf.grossProfit)} · %{pf.profitRate}</span>
                  </div>
                  <Bar value={pf.grossProfit} max={maxProfit} tone="bg-primary-500" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === 'tur' && (
        <Card className="p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-950">İş Türü Analizi</h2>
          <p className="mt-1 text-sm text-foreground-500">Hangi kategoriden kaç proje geldi ve toplam satış tutarı ne?</p>
          <div className="mt-5 space-y-4">
            {categoryList.map((c) => (
              <div key={c.category}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground-900">{c.category}</span>
                  <span className="flex items-center gap-2 text-foreground-500">
                    <Badge>{c.count} proje</Badge>
                    <span className="font-semibold text-foreground-900">{formatTL(c.total)}</span>
                  </span>
                </div>
                <Bar value={c.total} max={maxCategoryTotal} tone="bg-accent-500" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}